import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Sparkles } from 'lucide-react'
import { formatNumber } from '../utils'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import useLoginStore from '@/store/LoginStore'
import { updateAiSettings } from '@/lib/api/features/workspace-settings'
import { getAiUsage } from '@/lib/api/features/ai'

interface Props {
  workspaceSlug: string
}

/**
 * Card 1 — AI kill-switch + quota allocation.
 *
 * The toggle is a wrapper around the same `updateAiSettings` endpoint the
 * Workspace Settings page already calls; we replicate the optimistic-update +
 * rollback-on-error shape so both surfaces stay consistent and one toggle
 * disagreeing with the other never gets persisted. The quota row reads from
 * `/ai/admin/usage` (not `/ai/usage`) so it stays reachable while AI is
 * disabled — that's the admin's primary reason for visiting this tab.
 */
export function AiSettingsCard({ workspaceSlug }: Props) {
  const { t } = useTranslation('settings')
  const workspaces = useLoginStore((s) => s.workspaces)
  const setWorkspaces = useLoginStore((s) => s.setWorkspaces)
  const currentWorkspace = workspaces.find(
    (w) => w.workspaceSlug === workspaceSlug,
  )
  const aiEnabled = currentWorkspace?.aiEnabled ?? false
  const [isToggling, setIsToggling] = useState(false)

  // Quota limits sit alongside live usage on /ai/admin/usage. Refetch on
  // window focus so a server-side plan change picks up without a manual
  // reload, but otherwise lean on react-query's default staleness so the
  // toggle doesn't trigger a refetch storm.
  const usageQuery = useQuery({
    queryKey: ['ai-admin-usage', workspaceSlug],
    queryFn: () => getAiUsage(workspaceSlug),
    refetchOnWindowFocus: true,
  })

  const handleToggle = async (next: boolean) => {
    if (isToggling) return
    setIsToggling(true)
    // Optimistic store update. The Workspace Settings page uses the same
    // pattern; both views read from useLoginStore.workspaces so one toggle
    // movement here keeps both surfaces in sync without an explicit refetch.
    setWorkspaces(
      workspaces.map((w) =>
        w.workspaceSlug === workspaceSlug ? { ...w, aiEnabled: next } : w,
      ),
    )
    try {
      await updateAiSettings(workspaceSlug, { aiEnabled: next })
      toast.success(
        next ? t('aiSettings.toggle.enabled') : t('aiSettings.toggle.disabled'),
      )
    } catch (err: any) {
      setWorkspaces(
        workspaces.map((w) =>
          w.workspaceSlug === workspaceSlug ? { ...w, aiEnabled: !next } : w,
        ),
      )
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          t('aiSettings.toggle.errorFallback'),
      )
    } finally {
      setIsToggling(false)
    }
  }

  const limits = usageQuery.data?.limits
  const embeddingLimit = limits?.aiEmbeddingTokensMonthly
  const embeddingUsed = limits?.aiEmbeddingTokensUsed ?? 0
  const chatLimit = limits?.aiChatTokensMonthly
  const chatUsed = limits?.aiChatTokensUsed ?? 0
  // While trialing, the backend measures usage over the whole trial (the allowance
  // is per-trial, not per-month), so the quota rows must not say "this month".
  const isTrialWindow = usageQuery.data?.usageWindowIsTrial === true

  // A plan "funds AI" when it grants any AI token budget: null = unlimited,
  // > 0 = a budget. 0 = blocked (Free). Only funded plans (Individual / Team)
  // may toggle the kill-switch — enabling AI on a 0-budget plan just
  // rejects every call, so we gate the control on the plan instead.
  const isFunded = (v: number | null | undefined) =>
    v === null || (typeof v === 'number' && v > 0)
  const budgetsKnown = !usageQuery.isLoading && limits !== undefined
  const planFundsAi =
    budgetsKnown && (isFunded(chatLimit) || isFunded(embeddingLimit))

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="size-4 text-muted-foreground" />
          <CardTitle>{t('aiSettings.killSwitch.title')}</CardTitle>
        </div>
        <CardDescription>
          {t('aiSettings.killSwitch.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between rounded-md border p-4">
          <div>
            <Label htmlFor="ai-enabled-toggle" className="text-sm font-medium">
              {t('aiSettings.killSwitch.toggleLabel')}
            </Label>
            <p className="text-sm text-muted-foreground">
              {t('aiSettings.killSwitch.toggleHint')}
            </p>
          </div>
          <Switch
            id="ai-enabled-toggle"
            checked={aiEnabled && planFundsAi}
            disabled={isToggling || !planFundsAi}
            onCheckedChange={handleToggle}
          />
        </div>

        {budgetsKnown && !planFundsAi ? (
          <Alert>
            <AlertDescription>
              {t('aiSettings.killSwitch.planUpsell')}
            </AlertDescription>
          </Alert>
        ) : (
          aiEnabled &&
          embeddingLimit === 0 && (
            <Alert variant="destructive">
              <AlertDescription>
                {t('aiSettings.killSwitch.zeroQuotaWarning')}
              </AlertDescription>
            </Alert>
          )
        )}

        {planFundsAi && (
          <div className="grid gap-4 sm:grid-cols-2">
            <QuotaRow
              label={
                isTrialWindow
                  ? t('aiSettings.quota.embeddingTrial')
                  : t('aiSettings.quota.embedding')
              }
              used={embeddingUsed}
              limit={embeddingLimit}
              loading={usageQuery.isLoading}
            />
            <QuotaRow
              label={
                isTrialWindow
                  ? t('aiSettings.quota.chatTrial')
                  : t('aiSettings.quota.chat')
              }
              used={chatUsed}
              limit={chatLimit}
              loading={usageQuery.isLoading}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function QuotaRow({
  label,
  used,
  limit,
  loading,
}: {
  label: string
  used: number
  limit: number | null | undefined
  loading: boolean
}) {
  const { t } = useTranslation('settings')
  // Null = unlimited (no plan cap); 0 = blocked; otherwise number cap. The
  // backend collapses `-1` to unlimited at the QuotaService layer, so by the
  // time it lands here `null` is the only "unlimited" signal.
  const unlimited = limit === null || limit === undefined
  const percent =
    unlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100))

  return (
    <div className="rounded-md border p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      {loading ? (
        <p className="mt-2 text-sm text-muted-foreground">{t('aiSettings.quota.loading')}</p>
      ) : unlimited ? (
        <p className="mt-2 text-sm font-medium">{t('aiSettings.quota.unlimited')}</p>
      ) : (
        <>
          <p className="mt-2 text-sm font-medium tabular-nums">
            {formatNumber(used)} / {formatNumber(limit)}
          </p>
          <div
            className="mt-2 h-1.5 w-full overflow-hidden rounded bg-muted"
            aria-hidden
          >
            <div
              className="h-full bg-primary"
              style={{ width: `${percent}%` }}
            />
          </div>
        </>
      )}
    </div>
  )
}
